import "twin.macro";

import { useAuth0 } from "@auth0/auth0-react";
import { AnimatePresence } from "framer-motion";
import { cloneElement, Suspense } from "react";
import { Navigate, Outlet, useLocation, useRoutes } from "react-router-dom";

import { LoadingSpinner } from "./components/LoadingIndicator";
import { ClientDataLoader } from "./data-loaders/client-data-loader";
import { PostDetailDataLoader } from "./data-loaders/post-details-data-loader";
import PermissionProtectedRoute from "./PermissionProtectedRoute";
import PermissionsProvider from "./PermissionsContext";
import { AreasLayout } from "./routes/clients/Area";
import Clients from "./routes/clients/Clients";
import InformationsCard from "./routes/clients/Informations";
import { UsersLayout } from "./routes/clients/Users";
import { ErrorComponent } from "./routes/error/ErrorPage";
import { FourOhFour } from "./routes/error/FourOhFour";
import { FullPageLoadingScreen } from "./routes/FullPageLoadingScreen";
import Layout from "./routes/layout/Layout";
import { PostMaintenance } from "./routes/post-details/maintenance/post-maintenance";
import PostMonitoring from "./routes/post-details/PostMonitoring";
import QuickModuleAccess from "./routes/scan/quick-access";
import QuickPostAdd from "./routes/scan/quick-post-add";
import { SignIn } from "./routes/Signin";

const AuthenticationGuard = () => {
  const { isLoading, isAuthenticated, error } = useAuth0();

  if (error && !isLoading) {
    // TODO : handle error
    return <ErrorComponent subtitle={error.message} />;
  }

  if (!isLoading && !isAuthenticated) {
    return <Navigate to={"signin"} />;
  }

  if (isAuthenticated) {
    return (
      <PermissionsProvider>
        <Outlet />
      </PermissionsProvider>
    );
  }

  return <FullPageLoadingScreen show />;
};

type AnimatedRoutesProps = {
  isAppInitialized: boolean;
};

export const AnimatedRoutes = ({ isAppInitialized }: AnimatedRoutesProps) => {
  // Hook equivalent to <Routes />

  const Element = useRoutes([
    {
      path: "/",
      children: [
        {
          element: <AuthenticationGuard />,
          children: [
            {
              element: <PermissionProtectedRoute permission="admin" />,
              children: [
                {
                  index: true,
                  element: <Clients />,
                },
                {
                  path: "add-post",
                  element: <QuickPostAdd />,
                },
                {
                  path: "read-post",
                  element: <QuickModuleAccess />,
                },
              ],
            },
            {
              path: "clients/:clientId",
              element: (
                <Suspense
                  fallback={
                    <div tw="flex justify-center items-center h-full gap-2">
                      <LoadingSpinner tw="text-primary-800/50 size-20" />
                      <p tw="p-2 text-primary-800/50 text-sm">
                        Chargement des données du client...
                      </p>
                    </div>
                  }
                >
                  <ClientDataLoader />
                </Suspense>
              ),
              children: [
                {
                  path: "informations",
                  element: <InformationsCard />,
                },
                {
                  path: "team-members",
                  element: (
                    <Suspense
                      fallback={
                        <div tw="flex items-center justify-center p-4">
                          <div style={{ width: "42px", height: "42px" }}>
                            <LoadingSpinner tw="text-primary-800" />
                          </div>
                        </div>
                      }
                    >
                      <UsersLayout />
                    </Suspense>
                  ),
                },
                {
                  path: "areas",
                  element: <AreasLayout />,
                },
                {
                  path: "areas/:areaId/post/:postAddress",
                  element: <PostDetailDataLoader />,
                  children: [
                    {
                      path: "monitoring",
                      element: (
                        <Suspense
                          fallback={
                            <div tw="flex items-center justify-start p-4 gap-2">
                              <div style={{ width: "42px", height: "42px" }}>
                                <LoadingSpinner tw="text-primary-800/50" />
                              </div>
                              <p tw="p-2 text-primary-800/50 text-sm">
                                Chargement des données de monitoring ...
                              </p>
                            </div>
                          }
                        >
                          <PostMonitoring />
                        </Suspense>
                      ),
                    },
                    {
                      index: true,
                      element: <PostMaintenance />,
                    },
                    {
                      path: "history",
                      element: <div>Historique</div>,
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: "*",
          element: <FourOhFour />,
        },
        {
          path: "signin",
          element: <SignIn />,
        },
      ],
    },
  ]);

  const location = useLocation();

  if (!isAppInitialized) return null; // Loading screen

  if (!Element) return <div>Une erreur est survenue</div>; // Error page (404)

  return (
    <Layout>
      <AnimatePresence mode="wait">
        {cloneElement(Element, { key: location.pathname })}
      </AnimatePresence>
    </Layout>
  );
};
